import { useCallback, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    TextField,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    styled,
    Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { OrganizationType } from '../../../../../hooks/useSchoolInfo';
import AdminSettingsContainer from '../../../AdminSettingsContainer';

const AdvancedOptionsDiv = styled(Box)`
    margin-top: 24px;
`;

const AttributeNameInput = styled(TextField)`
    width: 400px;
`;

const FieldLabelDiv = styled(Box)`
    margin-top: 12px;
`;

type AdvancedOptionsProps = {
    emailAttributeName: string;
    courseIdAttributeName: string;
    courseIdPrefix: string;
    personIdAttributeName: string;
    personIdPrefix: string;
    debugMode: boolean;
    grantInstructorSystemRole: boolean;
    handleChange: (event: any) => void;
};

const AdvancedOptions = (props: AdvancedOptionsProps) => {
    const {
        emailAttributeName,
        courseIdAttributeName,
        courseIdPrefix,
        personIdAttributeName,
        personIdPrefix,
        debugMode,
        grantInstructorSystemRole,
        handleChange
    } = props;

    const { t } = useTranslation('admin_settings');
    const [debugDialogOpen, setDebugDialogOpen] = useState<boolean>(false);
    const { school } = useContainer(AdminSettingsContainer);

    const handleGrantInstructorsSystemRoleChange = useCallback(() => {
        handleChange({
            target: {
                name: 'grant_instructor_system_role',
                checked: !grantInstructorSystemRole
            }
        });
    }, [handleChange, grantInstructorSystemRole]);

    const handleConfirmDebugMode = () => {
        handleChange({ target: { name: 'debug_mode', checked: true } });
        setDebugDialogOpen(false);
    };

    const handleDebugSwitchClick = () => {
        if (debugMode) {
            handleChange({ target: { name: 'debug_mode', checked: false } });
            return;
        }
        setDebugDialogOpen(true);
    };

    return (
        <AdvancedOptionsDiv>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography>{t('advanced-settings')}</Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <FieldLabelDiv>
                        <Typography>{t('email-attribute-name')}</Typography>
                        <AttributeNameInput
                            size="small"
                            name="email_attribute_name"
                            value={emailAttributeName}
                            onChange={handleChange}
                            helperText={t('leave-blank-for-default')}
                        />
                    </FieldLabelDiv>

                    <FieldLabelDiv>
                        <Typography>{t('course-id-attribute-name')}</Typography>
                        <AttributeNameInput
                            size="small"
                            name="course_id_attribute_name"
                            value={courseIdAttributeName}
                            onChange={handleChange}
                            helperText={t('leave-blank-for-default')}
                        />
                    </FieldLabelDiv>

                    <FieldLabelDiv>
                        <Typography>{t('course-id-prefix')}</Typography>
                        <AttributeNameInput
                            size="small"
                            name="course_id_prefix"
                            value={courseIdPrefix}
                            onChange={handleChange}
                            helperText={t('leave-blank-for-default')}
                        />
                    </FieldLabelDiv>

                    <FieldLabelDiv>
                        <Typography>{t('person-id-attribute-name')}</Typography>
                        <AttributeNameInput
                            size="small"
                            name="person_id_attribute_name"
                            value={personIdAttributeName}
                            onChange={handleChange}
                            helperText={t('leave-blank-for-default')}
                        />
                    </FieldLabelDiv>

                    <FieldLabelDiv>
                        <Typography>{t('person-id-prefix')}</Typography>
                        <AttributeNameInput
                            size="small"
                            name="person_id_prefix"
                            value={personIdPrefix}
                            onChange={handleChange}
                            helperText={t('leave-blank-for-default')}
                        />
                    </FieldLabelDiv>

                    <FieldLabelDiv>
                        <Typography>{t('debug-switch-label')}</Typography>
                        <Switch name="debug_mode" checked={debugMode} onChange={handleDebugSwitchClick} />
                    </FieldLabelDiv>

                    {school?.type === OrganizationType.User && (
                        <FieldLabelDiv>
                            <Typography>{t('grant-instructor-system-role')}</Typography>
                            <Switch
                                name="grant_instructor_system_role"
                                checked={grantInstructorSystemRole}
                                onChange={handleGrantInstructorsSystemRoleChange}
                            />
                        </FieldLabelDiv>
                    )}
                </AccordionDetails>
            </Accordion>

            <Dialog open={debugDialogOpen} onClose={() => setDebugDialogOpen(false)}>
                <DialogTitle>{t('debug-dialog-title')}</DialogTitle>

                <DialogContent>
                    <DialogContentText>{t('debug-dialog-body')}</DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button variant="outlined" onClick={() => setDebugDialogOpen(false)}>
                        {t('cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleConfirmDebugMode}>
                        {t('activate')}
                    </Button>
                </DialogActions>
            </Dialog>
        </AdvancedOptionsDiv>
    );
};

export default AdvancedOptions;
